import { COMPONENT_TYPE_LABELS } from '../constants';
import {
  ALLOWED_PROCESSOR_POSITIONS,
  COMPLEXITIES,
  COMPONENT_CATEGORIES,
  COMPONENT_FEATURES,
  COMPONENT_TYPE_OTHER,
  COMPONENT_TYPES,
  DATA_TYPE_SUPPORTS,
  DEFAULT_BUCKET_STAGES,
  LOGGERS,
  REPOSITORY_TYPES,
  STAGING_STORAGE_INPUTS,
  STAGING_STORAGE_OUTPUTS,
  UI_OPTIONS,
} from '../vocabulary';

/**
 * Select options for the edit form.
 *
 * The accepted **values** all come from `@keboola/api-client/developerPortal` — an agent driving the API
 * needs those, and duplicating them here is how the two drift. Only the human labels live here, plus the
 * projection into the `{ value, label }` shape `SelectField` takes.
 */

type Option = { value: string; label: string };

/** Titlecases a hyphenated or underscored slug: `workspace-snowflake` → `Workspace snowflake`. */
const humanize = (value: string): string => {
  const spaced = value.replaceAll(/[-_]/g, ' ');
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
};

const toOptions = (values: readonly string[], labels: Record<string, string> = {}): Option[] =>
  values.map((value) => ({ value, label: labels[value] ?? humanize(value) }));

/** `other` is selectable here even though it is not a `COMPONENT_TYPES` member — see the constant. */
export const TYPE_OPTIONS = toOptions(
  [...COMPONENT_TYPES, COMPONENT_TYPE_OTHER],
  COMPONENT_TYPE_LABELS,
);

export const REPOSITORY_TYPE_OPTIONS = toOptions(REPOSITORY_TYPES, {
  ecr: 'AWS ECR',
  quay: 'Quay',
  dockerhub: 'Docker Hub',
  builder: 'Keboola Builder',
});

export const COMPLEXITY_OPTIONS = toOptions(COMPLEXITIES);

/** Both staging lists share their labels; the output list is the input list minus `s3` and `abs`. */
const STAGING_STORAGE_LABELS: Record<string, string> = {
  local: 'Local',
  s3: 'AWS S3',
  abs: 'ABS',
  none: 'None',
  'workspace-snowflake': 'Workspace Snowflake',
  'workspace-synapse': 'Workspace Synapse',
  'workspace-redshift': 'Workspace Redshift',
  'workspace-exasol': 'Workspace Exasol',
  'workspace-teradata': 'Workspace Teradata',
  'workspace-bigquery': 'Workspace BigQuery',
};

export const STAGING_STORAGE_INPUT_OPTIONS = toOptions(
  STAGING_STORAGE_INPUTS,
  STAGING_STORAGE_LABELS,
);
export const STAGING_STORAGE_OUTPUT_OPTIONS = toOptions(
  STAGING_STORAGE_OUTPUTS,
  STAGING_STORAGE_LABELS,
);

export const DATA_TYPE_SUPPORT_OPTIONS = toOptions(DATA_TYPE_SUPPORTS);
export const ALLOWED_PROCESSOR_POSITION_OPTIONS = toOptions(ALLOWED_PROCESSOR_POSITIONS);
export const LOGGER_OPTIONS = toOptions(LOGGERS, { standard: 'Standard', gelf: 'Gelf' });
export const DEFAULT_BUCKET_STAGE_OPTIONS = toOptions(DEFAULT_BUCKET_STAGES, {
  in: 'In',
  out: 'Out',
});

/** Multi-selects take bare strings; the value *is* the label for these. */
export const CATEGORY_ITEMS: readonly string[] = COMPONENT_CATEGORIES;
export const FEATURE_ITEMS: readonly string[] = COMPONENT_FEATURES;

/**
 * The UI-option list is **open**, not a closed enum: a component registered before an option was retired
 * still carries it, and dropping it from the picker would silently delete it on the next save. So the
 * offered list is the shipped vocabulary unioned with whatever the component already has.
 */
export const uiOptionItems = (current: readonly string[]): string[] => [
  ...new Set([...UI_OPTIONS, ...current]),
];
